import React, { useMemo } from 'react';

import { useClassnames } from '@lumapps/classnames';
import { CommunityLink } from '@lumapps/communities/components/CommunityLink';
import { CommunityLinkRef } from '@lumapps/communities/types';
import { ContentTypes } from '@lumapps/content-types/types';
import { inferContentTypeFromResourceType } from '@lumapps/content-types/utils';
import { ContentLink } from '@lumapps/contents/components/ContentLink';
import { ContentLinkRef } from '@lumapps/contents/types';
import { Icon, Text } from '@lumapps/lumx/react';
import { addBaseSlug } from '@lumapps/router/utils';
import { useDynamicIcon } from '@lumapps/utils/hooks/useDynamicIcon';
import { useHover } from '@lumapps/utils/hooks/useHover';
import { hasProtocol } from '@lumapps/utils/string/isUrl';
import type { WidgetGlobalStyles } from '@lumapps/widget-styles/types';
import { FlexDirection } from '@lumapps/widget-styles/types/enum';
import { computeFooterStyles } from '@lumapps/widget-styles/utils/computeFooterStyles';

import { Footer } from '../../types';

import './index.scss';

interface WidgetFooterProps extends Footer {
    /** Parent widget root style */
    parentStyle?: WidgetGlobalStyles;
}

const CLASSNAME = 'widget-footer';
export const WidgetFooter: React.FC<WidgetFooterProps> = (props) => {
    const { label, url, icon, style, resourceId, resourceType, site, renderingType, parentStyle } = props;
    const { iconPath } = useDynamicIcon({ icon: icon?.trim() || '' });
    const { isHovered, registerElement } = useHover();
    const { element, block } = useClassnames(CLASSNAME);

    const styles = useMemo(() => computeFooterStyles(style, isHovered, parentStyle), [isHovered, style, parentStyle]);
    const flexDirection = styles?.wrapper?.flexDirection || 'row';
    const isExternal = hasProtocol(url);
    const isRelative = !isExternal && !url?.startsWith('#');

    const linkProps = {
        ref: registerElement,
        target: isExternal ? '_blank' : undefined,
        rel: isExternal ? 'noopener noreferrer' : undefined,
        className: element(
            'wrapper',
            {
                'layout-bottom': flexDirection === FlexDirection.columnReverse,
                'layout-left': flexDirection === FlexDirection.row,
                'layout-right': flexDirection === FlexDirection.rowReverse,
                'layout-top': flexDirection === FlexDirection.column,
            },
            ['widget-wrapper'],
        ),
        style: styles.wrapper,
    };
    const content = (
        <>
            {icon && <Icon className={element('icon')} icon={iconPath} style={styles.icon} />}
            <Text as="span" className={element('label')} style={styles.label}>
                {label}
            </Text>
        </>
    );

    const { Link, to } = useMemo(() => {
        if (!url || !resourceId || !resourceType) {
            return {};
        }

        const instance = { id: site?.siteId, slug: site?.slug };

        const parsedUrl = new URL(url as string, window.location.href);
        const to: ContentLinkRef = {
            id: resourceId,
            type: inferContentTypeFromResourceType(resourceType),
            instance,
            slug: parsedUrl.pathname,
            query: parsedUrl.search,
        };

        if (to.type === ContentTypes.COMMUNITY) {
            return {
                Link: CommunityLink,
                to: {
                    id: resourceId,
                    instance,
                    // Keep only the last part of the slug
                    slug: to.slug?.split('/')?.pop(),
                    // Add community rendering type
                    renderingType,
                } as CommunityLinkRef,
            };
        }

        return { Link: ContentLink, to };
    }, [renderingType, resourceId, resourceType, site?.siteId, site?.slug, url]);

    return (
        <div className={block()} style={styles.main}>
            {Link && to ? (
                <Link {...linkProps} to={to}>
                    {content}
                </Link>
            ) : (
                <a {...linkProps} href={isRelative ? addBaseSlug(url as string) : url}>
                    {content}
                </a>
            )}
        </div>
    );
};

WidgetFooter.displayName = 'WidgetFooter';
